import React, { useState, useEffect, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useDispatch } from 'react-redux';
import { handleGetRequest } from '../../../service/GetTemplate';
import { endPoints } from '../../../Config';
import { formatLocalizedDateTime, formatTimeFromMinutes } from '../../../utilities/formatTime';
import { exportToExcel } from '../../../utilities/exportToExcel';
import { toast } from 'react-toastify';
import { inReportDateRange } from '../reportFilters';
import { ROUTE_OPTIONS } from '../../../utilities/constants';
import usePreferredLanguage from '../../../utilities/usePreferredLanguage';
import { translateUiText, translateUiToken } from '../../../utilities/siteTranslations';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

// Filter options - similar to List of Failures
const failureTypeOptions = [
    { label: 'All Types', value: null },
    { label: 'Electrical Coffee', value: 'Electrical Coffee' },
    { label: 'Electrical Snack', value: 'Electrical Snack' },
    { label: 'Exterior Coffee', value: 'Exterior Coffee' },
    { label: 'Exterior Snack', value: 'Exterior Snack' },
    { label: 'Hydraulics Coffee', value: 'Hydraulics Coffee' },
    { label: 'Hydraulics Snack', value: 'Hydraulics Snack' },
    { label: 'Mechanical Coffee', value: 'Mechanical Coffee' },
    { label: 'Mechanical Snack', value: 'Mechanical Snack' }
];

const serviceAreaOptions = [
    { label: 'All Areas', value: null },
    { label: 'Downtown', value: 'Downtown' },
    { label: 'Uptown', value: 'Uptown' },
    { label: 'Midtown', value: 'Midtown' }
];

const routeOptions = ROUTE_OPTIONS;

const machineTypeOptions = [
    { label: 'All Types', value: null },
    { label: 'SnackMaster 5000', value: 'SnackMaster 5000' },
    { label: 'BeveragePro 3000', value: 'BeveragePro 3000' }
];

const CaseTrackingDashboard = () => {
    const preferredLanguage = usePreferredLanguage();
    const t = (value) => translateUiText(value, preferredLanguage);
    const tt = (value) => translateUiToken(value, preferredLanguage);
    const [allCases, setAllCases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState({
        startDate: null,
        endDate: null,
        failureType: null,
        serviceArea: null,
        route: null,
        customer: '',
        machineId: '',
        machineType: null,
        status: null
    });
    const dispatch = useDispatch();

    const loadCases = async () => {
        setLoading(true);
        const response = await handleGetRequest(endPoints.getCaseTracking(''), true, false, dispatch);
        setLoading(false);
        if (response?.data) {
            const apiCases = response.data.cases || [];

            const mappedCases = apiCases.map((c) => {
                const labelStatusMap = {
                    open: 'Open',
                    in_progress: 'In Progress',
                    awaiting_customer: 'Awaiting Customer Response',
                    close: 'Resolved',
                    forwarded: 'Open',
                    pending: 'Open'
                };
                const displayStatus = labelStatusMap[c.status] || c.status || 'Open';

                const priorityValue =
                    displayStatus === 'Open' ? 'High' : displayStatus === 'Resolved' ? 'Low' : 'Medium';

                // Calculate time elapsed in minutes
                const createdAt = c.createdAt ? new Date(c.createdAt) : new Date();
                const now = new Date();
                const timeElapsedMinutes = Math.floor((now - createdAt) / (1000 * 60));

                    return {
                        id: c._id,
                        caseId: c._id ? `CASE-${c._id.slice(-6).toUpperCase()}` : '',
                        customerName: c.customer || c.whatsAppNumber || '-',
                        machineId: c.machine?.serialNumber || c.machine?.deviceId || c.machineId || '-',
                        machineType: c.machineType || '-',
                    status: displayStatus,
                    priority: priorityValue,
                    assignedTo: c.assignedTo || '-',
                    team: c.team || '-',
                    createdAt: c.createdAt,
                    updatedAt: c.updatedAt,
                    timeElapsedMinutes: timeElapsedMinutes,
                    route: c.route || '-',
                    serviceArea: c.serviceArea || '-',
                    failureType: c.failureType || '-'
                };
            });

            setAllCases(mappedCases);
        }
    };

    useEffect(() => {
        loadCases();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const applyFilters = () => loadCases();

    const resetFilters = () => {
        setFilters({
            startDate: null,
            endDate: null,
            failureType: null,
            serviceArea: null,
            route: null,
            customer: '',
            machineId: '',
            machineType: null,
            status: null
        });
        loadCases();
    };

    // Table and export share the exact same selected filters, including full calendar days.
    const filteredCases = useMemo(() => {
        let filtered = allCases.filter((row) => inReportDateRange(row.createdAt, filters.startDate, filters.endDate));

        if (filters.failureType) {
            filtered = filtered.filter((c) => c.failureType === filters.failureType);
        }
        if (filters.serviceArea) {
            filtered = filtered.filter((c) => c.serviceArea === filters.serviceArea);
        }
        if (filters.route) {
            filtered = filtered.filter((c) => c.route === filters.route);
        }
        if (filters.customer) {
            filtered = filtered.filter((c) => String(c.customerName ?? '').toLowerCase().includes(filters.customer.trim().toLowerCase()));
        }
        if (filters.machineId) {
            filtered = filtered.filter((c) => String(c.machineId ?? '').toLowerCase().includes(filters.machineId.trim().toLowerCase()));
        }
        if (filters.machineType) {
            filtered = filtered.filter((c) => c.machineType === filters.machineType);
        }
        if (filters.status) {
            filtered = filtered.filter((c) => c.status === filters.status);
        }

        return filtered;
    }, [allCases, filters]);

    const stats = useMemo(() => ({
        total: filteredCases.length,
        open: filteredCases.filter((row) => row.status === 'Open').length,
        inProgress: filteredCases.filter((row) => row.status === 'In Progress').length,
        awaitingResponse: filteredCases.filter((row) => row.status === 'Awaiting Customer Response').length,
        resolved: filteredCases.filter((row) => row.status === 'Resolved').length
    }), [filteredCases]);

    const handleSummaryCardClick = (statusFilter) => {
        setFilters({ ...filters, status: statusFilter });
    };

    const exportToExcelHandler = () => {
        const columns = [
            { key: 'caseId', header: t('Case ID'), width: 15 },
            { key: 'customerName', header: t('Customer'), width: 20 },
            { key: 'machineId', header: t('Machine ID'), width: 15 },
            { key: 'machineType', header: t('Machine Type'), width: 20 },
            { key: 'status', header: t('Status'), width: 20, formatter: tt },
            { key: 'priority', header: t('Priority'), width: 12, formatter: tt },
            { key: 'assignedTo', header: t('Assigned To'), width: 18 },
            { key: 'team', header: t('Team'), width: 15 },
            { key: 'timeElapsed', header: t('Time Elapsed'), width: 20 },
            { key: 'createdAt', header: t('Created At'), width: 20, formatter: (value) => formatLocalizedDateTime(value, preferredLanguage) },
            { key: 'updatedAt', header: t('Last Updated'), width: 20, formatter: (value) => formatLocalizedDateTime(value, preferredLanguage) }
        ];

        const exportData = filteredCases.map((c) => ({
            ...c,
            timeElapsed: formatTimeFromMinutes(c.timeElapsedMinutes, preferredLanguage)
        }));

        if (!exportData.length) {
            toast.info(t('There is no data to export for this section.'));
            return;
        }
        try {
            exportToExcel(exportData, preferredLanguage === 'es' ? 'Reporte_Seguimiento_Casos' : 'Case_Tracking_Report', columns);
        } catch {
            toast.error(t('Failed to export data. Please try again.'));
        }
    };

    const statusBodyTemplate = (rowData) => {
        const statusColors = {
            Open: '#FF7171',
            'In Progress': '#4CAF50',
            'Awaiting Customer Response': '#FFA726',
            Resolved: '#2196F3'
        };
        return <span style={{ color: statusColors[rowData.status] || '#000', fontWeight: 'bold' }}>{tt(rowData.status)}</span>;
    };

    const priorityBodyTemplate = (rowData) => {
        const priorityColors = {
            High: '#F44336',
            Medium: '#FF9800',
            Low: '#4CAF50'
        };
        return <span style={{ color: priorityColors[rowData.priority] || '#000', fontWeight: 'bold' }}>{tt(rowData.priority)}</span>;
    };

    const dateBodyTemplate = (rowData, field) => {
        if (!rowData[field]) return '-';
        return formatLocalizedDateTime(rowData[field], preferredLanguage);
    };

    const timeElapsedBodyTemplate = (rowData) => {
        return <span>{formatTimeFromMinutes(rowData.timeElapsedMinutes, preferredLanguage)}</span>;
    };

    const statusOptions = [
        { label: t('All Statuses'), value: null },
        { label: t('Open'), value: 'Open' },
        { label: t('In Progress'), value: 'In Progress' },
        { label: t('Awaiting Customer Response'), value: 'Awaiting Customer Response' },
        { label: t('Resolved'), value: 'Resolved' }
    ];

    return (
        <div>
            {/* Filters Section */}
            <div className="card mb-4">
                <h4 className="mb-3">{t('Filters')}</h4>
                <div className="grid">
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Start Date')}</label>
                        <Calendar 
                            value={filters.startDate} 
                            onChange={(e) => setFilters({ ...filters, startDate: e.value })} 
                            dateFormat="yy-mm-dd" 
                            placeholder={t('Select Start Date')} 
                            showIcon 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('End Date')}</label>
                        <Calendar 
                            value={filters.endDate} 
                            onChange={(e) => setFilters({ ...filters, endDate: e.value })} 
                            dateFormat="yy-mm-dd" 
                            placeholder={t('Select End Date')} 
                            showIcon 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Status')}</label>
                        <Dropdown 
                            value={filters.status} 
                            options={statusOptions} 
                            onChange={(e) => setFilters({ ...filters, status: e.value })} 
                            placeholder={t('Select Status')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Route')}</label>
                        <Dropdown 
                            value={filters.route} 
                            options={routeOptions} 
                            onChange={(e) => setFilters({ ...filters, route: e.value })} 
                            placeholder={t('Select Route')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Failure Type')}</label>
                        <Dropdown 
                            value={filters.failureType} 
                            options={failureTypeOptions.map((option) => ({ ...option, label: t(option.label) }))} 
                            onChange={(e) => setFilters({ ...filters, failureType: e.value })} 
                            placeholder={t('Select Failure Type')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Service Area')}</label>
                        <Dropdown 
                            value={filters.serviceArea} 
                            options={serviceAreaOptions.map((option) => ({ ...option, label: t(option.label) }))} 
                            onChange={(e) => setFilters({ ...filters, serviceArea: e.value })} 
                            placeholder={t('Select Service Area')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Customer')}</label>
                        <InputText 
                            value={filters.customer} 
                            onChange={(e) => setFilters({ ...filters, customer: e.target.value })} 
                            placeholder={t('Enter Customer Name')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Machine ID')}</label>
                        <InputText 
                            value={filters.machineId} 
                            onChange={(e) => setFilters({ ...filters, machineId: e.target.value })} 
                            placeholder={t('Enter Machine ID')} 
                            className="w-full" 
                        />
                    </div>
                    <div className="col-12 md:col-3">
                        <label className="block mb-2">{t('Machine Type')}</label>
                        <Dropdown 
                            value={filters.machineType} 
                            options={machineTypeOptions.map((option) => ({ ...option, label: t(option.label) }))} 
                            onChange={(e) => setFilters({ ...filters, machineType: e.value })} 
                            placeholder={t('Select Machine Type')} 
                            className="w-full" 
                        />
                    </div>
                </div>
                <div className="flex gap-2 mt-3">
                    <Button label={t('Apply Filters')} icon="pi pi-filter" onClick={applyFilters} className="p-button-primary" />
                    <Button label={t('Reset')} icon="pi pi-refresh" onClick={resetFilters} className="p-button-secondary" />
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid mb-4">
                <div className="col-12 md:col-2">
                    <Card style={{ cursor: 'pointer' }} onClick={() => handleSummaryCardClick(null)}>
                        <div className="text-center">
                            <div className="text-2xl font-bold text-primary">{stats.total}</div>
                            <div className="text-color-secondary">{t('Total Cases')}</div>
                        </div>
                    </Card>
                </div>
                <div className="col-12 md:col-2">
                    <Card style={{ cursor: 'pointer' }} onClick={() => handleSummaryCardClick('Open')}>
                        <div className="text-center">
                            <div className="text-2xl font-bold" style={{ color: '#FF7171' }}>
                                {stats.open}
                            </div>
                            <div className="text-color-secondary">{t('Open')}</div>
                        </div>
                    </Card>
                </div>
                <div className="col-12 md:col-2">
                    <Card style={{ cursor: 'pointer' }} onClick={() => handleSummaryCardClick('In Progress')}>
                        <div className="text-center">
                            <div className="text-2xl font-bold" style={{ color: '#4CAF50' }}>
                                {stats.inProgress}
                            </div>
                            <div className="text-color-secondary">{t('In Progress')}</div>
                        </div>
                    </Card>
                </div>
                <div className="col-12 md:col-2">
                    <Card style={{ cursor: 'pointer' }} onClick={() => handleSummaryCardClick('Awaiting Customer Response')}>
                        <div className="text-center">
                            <div className="text-2xl font-bold" style={{ color: '#FFA726' }}>
                                {stats.awaitingResponse}
                            </div>
                            <div className="text-color-secondary">{t('Awaiting Response')}</div>
                        </div>
                    </Card>
                </div>
                <div className="col-12 md:col-2">
                    <Card style={{ cursor: 'pointer' }} onClick={() => handleSummaryCardClick('Resolved')}>
                        <div className="text-center">
                            <div className="text-2xl font-bold" style={{ color: '#2196F3' }}>
                                {stats.resolved}
                            </div>
                            <div className="text-color-secondary">{t('Resolved')}</div>
                        </div>
                    </Card>
                </div>
            </div>

            {/* Data Table */}
            <div className="card">
                <div className="flex justify-content-between align-items-center mb-3">
                    <h4>{t('All Cases')}</h4>
                    <Button 
                        label={t('Export to Excel')} 
                        icon="pi pi-file-excel" 
                        onClick={exportToExcelHandler}
                        disabled={loading}
                        className="p-button-success p-button-sm" 
                    />
                </div>
                <DataTable 
                    value={filteredCases} 
                    paginator 
                    rows={10} 
                    rowsPerPageOptions={[5, 10, 20]} 
                    emptyMessage={t('No cases found.')} 
                    responsiveLayout="scroll"
                >
                    <Column field="caseId" header={t('Case ID')} />
                    <Column field="customerName" header={t('Customer')} />
                    <Column field="machineId" header={t('Machine ID')} />
                    <Column field="machineType" header={t('Machine Type')} />
                    <Column field="status" header={t('Status')} body={statusBodyTemplate} />
                    <Column field="priority" header={t('Priority')} body={priorityBodyTemplate} />
                    <Column field="assignedTo" header={t('Assigned To')} />
                    <Column field="team" header={t('Team')} />
                    <Column field="timeElapsedMinutes" header={t('Time Elapsed')} body={timeElapsedBodyTemplate} />
                    <Column field="createdAt" header={t('Created At')} body={(rowData) => dateBodyTemplate(rowData, 'createdAt')} />
                    <Column field="updatedAt" header={t('Last Updated')} body={(rowData) => dateBodyTemplate(rowData, 'updatedAt')} />
                </DataTable>
            </div>
        </div>
    );
};

export default CaseTrackingDashboard;
